import {
	FunctionComponent,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useDebounced, ConfirmModal } from "@animech/ui";
import * as Styled from "./developer-panel.style";
import { getReverseSkuTable } from "./reverse-sku-table";
import { useDevTools } from "./dev-tools-context";
import { useUiData } from "src/ui";
import { ApiArticles } from "src/api/api-data-manager";
import { ApiDataContext, ApiNotification } from "src/api";

const widgets = ["none", "ColorPicker"];
type WidgetOption = (typeof widgets)[number];

const apiNotificationCases = {
	"save success": {
		status: "success",
		requestType: "save",
		name: "Save",
		message: "Design successfully saved",
	},
	"save error": {
		status: "error",
		requestType: "save",
		name: "AxiosError",
		message: "Request failed with status code 500",
	},
	"email error": {
		status: "error",
		requestType: "email",
		name: "AxiosError",
		message: "Request failed with status code 500",
	},
	"pdf error": {
		status: "error",
		requestType: "pdf",
		name: "AxiosError",
		message: "Request failed with status code 500",
	},
	"open error": {
		status: "error",
		requestType: "open",
		name: "AxiosError",
		message: "Request failed with status code 404",
	},
} satisfies Record<string, ApiNotification>;
type NotificationCase = keyof typeof apiNotificationCases;
const notificationCases = Object.keys(
	apiNotificationCases,
) as NotificationCase[];

type DeveloperPanelState = {
	panel: {
		visible: boolean;
	};
	widget: {
		selected: WidgetOption;
		mounted: boolean;
		visible: boolean;
	};
};

const longLorem = `Lorem ipsum dolor sit amet, consectetur adipiscing elit,
	sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim
	ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip
	ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate
	velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat
	cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.`;

type WorkingArticle = {
	sku: string;
	name: string;
	priceWithVat: number;
	priceWithoutVat: number;
	ean: string | null;
	vat: number;
	packSize: number;
	productKey: string;
	inStock: boolean;
};

type SortKey = keyof Pick<
	WorkingArticle,
	"sku" | "name" | "priceWithVat" | "priceWithoutVat" | "productKey" | "inStock"
>;

type SortDirection = "asc" | "desc";

type SortState = {
	key: SortKey;
	direction: SortDirection;
};

const SORT_COLUMNS: { key: SortKey; label: string }[] = [
	{ key: "sku", label: "SKU" },
	{ key: "name", label: "Name" },
	{ key: "priceWithVat", label: "Price (inc VAT)" },
	{ key: "priceWithoutVat", label: "Price (ex VAT)" },
	{ key: "productKey", label: "Product / Theme" },
	{ key: "inStock", label: "In Stock" },
];

function compareSortValues(
	a: WorkingArticle,
	b: WorkingArticle,
	key: SortKey,
	direction: SortDirection,
): number {
	const av = a[key];
	const bv = b[key];
	let cmp = 0;

	if (typeof av === "string" && typeof bv === "string") {
		cmp = av.localeCompare(bv);
	} else if (typeof av === "number" && typeof bv === "number") {
		cmp = av - bv;
	} else if (typeof av === "boolean" && typeof bv === "boolean") {
		cmp = (av ? 1 : 0) - (bv ? 1 : 0);
	}

	return direction === "desc" ? -cmp : cmp;
}

const DEFAULT_PANEL_WIDTH = 1500;

const StockEditor: FunctionComponent<{
	onClose: () => void;
}> = ({ onClose }) => {
	const devTools = useDevTools();
	const stockEditor = devTools?.stockEditor;
	const [articles, setArticles] = useState<WorkingArticle[]>([]);
	const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH);
	const isDragging = useRef(false);
	const cleanupRef = useRef<(() => void) | null>(null);
	const applyRef = useRef(stockEditor?.applyArticles);
	applyRef.current = stockEditor?.applyArticles;

	// Sort state
	const [sort, setSort] = useState<SortState>({
		key: "name",
		direction: "asc",
	});

	// Selection state (tracks SKUs)
	const [selectedSkus, setSelectedSkus] = useState<Set<string>>(new Set());
	const lastClickedIndexRef = useRef<number | null>(null);

	const sortedArticles = useMemo(() => {
		const sorted = [...articles];
		sorted.sort((a, b) => compareSortValues(a, b, sort.key, sort.direction));
		return sorted;
	}, [articles, sort]);

	useEffect(() => {
		if (!stockEditor) {
			return;
		}
		const apiArticles = stockEditor.getApiArticles();
		setArticles(apiArticlesToWorking(apiArticles));
	}, [stockEditor]);

	const debouncedApply = useDebounced((updated: WorkingArticle[]) => {
		if (!applyRef.current) {
			return;
		}
		applyRef.current(workingToApiArticles(updated));
	}, 300);

	const applyNow = useCallback((updated: WorkingArticle[]) => {
		if (!applyRef.current) {
			return;
		}
		applyRef.current(workingToApiArticles(updated));
	}, []);

	// Debounced apply whenever articles change (after initial load)
	useEffect(() => {
		if (articles.length > 0) {
			debouncedApply(articles);
		}
	}, [articles, debouncedApply]);

	// Cleanup event listeners on unmount
	useEffect(() => {
		return () => {
			if (cleanupRef.current) {
				cleanupRef.current();
			}
		};
	}, []);

	if (!stockEditor) {
		return null;
	}

	const handleSort = (key: SortKey) => {
		setSort((prev) =>
			prev.key === key
				? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
				: { key, direction: "asc" },
		);
	};

	const handleRowClick = (
		sku: string,
		sortedIndex: number,
		e: React.MouseEvent,
	) => {
		// Don't select when clicking on inputs
		if ((e.target as HTMLElement).tagName === "INPUT") {
			return;
		}

		setSelectedSkus((prev) => {
			const next = new Set(prev);

			if (e.shiftKey && lastClickedIndexRef.current !== null) {
				// Shift-click: range select
				const start = Math.min(lastClickedIndexRef.current, sortedIndex);
				const end = Math.max(lastClickedIndexRef.current, sortedIndex);
				for (let i = start; i <= end; i++) {
					next.add(sortedArticles[i].sku);
				}
			} else if (e.metaKey || e.ctrlKey) {
				// Cmd/Ctrl-click: toggle individual
				if (next.has(sku)) {
					next.delete(sku);
				} else {
					next.add(sku);
				}
			} else {
				// Plain click: select only this row
				next.clear();
				next.add(sku);
			}

			return next;
		});

		lastClickedIndexRef.current = sortedIndex;
	};

	const handleSelectAll = () => {
		setSelectedSkus(new Set(sortedArticles.map((a) => a.sku)));
	};

	const handleSelectNone = () => {
		setSelectedSkus(new Set());
		lastClickedIndexRef.current = null;
	};

	const handleBulkToggleStock = (inStock: boolean) => {
		setArticles((prev) =>
			prev.map((a) => (selectedSkus.has(a.sku) ? { ...a, inStock } : a)),
		);
	};

	const handlePriceChange = (
		sku: string,
		field: "priceWithVat" | "priceWithoutVat",
		value: string,
	) => {
		const num = parseFloat(value);
		if (isNaN(num)) {
			return;
		}
		const targets =
			selectedSkus.has(sku) && selectedSkus.size > 1
				? selectedSkus
				: new Set([sku]);
		setArticles((prev) =>
			prev.map((a) => (targets.has(a.sku) ? { ...a, [field]: num } : a)),
		);
	};

	const handleToggleInStock = (sku: string) => {
		setArticles((prev) => {
			const toggled = !prev.find((a) => a.sku === sku)?.inStock;
			const targets =
				selectedSkus.has(sku) && selectedSkus.size > 1
					? selectedSkus
					: new Set([sku]);
			return prev.map((a) =>
				targets.has(a.sku) ? { ...a, inStock: toggled } : a,
			);
		});
	};

	const handleReset = () => {
		const original = stockEditor.getOriginalApiArticles();
		const working = apiArticlesToWorking(original);
		setArticles(working);
		applyNow(working);
		setSelectedSkus(new Set());
		lastClickedIndexRef.current = null;
	};

	const handleResizeStart = (e: React.MouseEvent) => {
		e.preventDefault();
		isDragging.current = true;
		const startX = e.clientX;
		const startWidth = panelWidth;

		const onMouseMove = (moveEvent: MouseEvent) => {
			if (!isDragging.current) {
				return;
			}
			const delta = moveEvent.clientX - startX;
			const newWidth = Math.max(600, startWidth + delta * 2);
			setPanelWidth(newWidth);
		};

		const onMouseUp = () => {
			isDragging.current = false;
			document.removeEventListener("mousemove", onMouseMove);
			document.removeEventListener("mouseup", onMouseUp);
			cleanupRef.current = null;
		};

		// Clean up any previous listeners before adding new ones
		if (cleanupRef.current) {
			cleanupRef.current();
		}

		document.addEventListener("mousemove", onMouseMove);
		document.addEventListener("mouseup", onMouseUp);
		cleanupRef.current = () => {
			document.removeEventListener("mousemove", onMouseMove);
			document.removeEventListener("mouseup", onMouseUp);
		};
	};

	const sortIndicator = (key: SortKey) =>
		sort.key === key ? (
			<Styled.SortIndicator>
				{sort.direction === "asc" ? "\u25B2" : "\u25BC"}
			</Styled.SortIndicator>
		) : null;

	return (
		<Styled.StockEditorOverlay onClick={onClose}>
			<Styled.StockEditorPanel
				panelWidth={panelWidth}
				onClick={(e) => e.stopPropagation()}
			>
				<Styled.ResizeHandle onMouseDown={handleResizeStart} />
				<Styled.StockEditorHeader>
					<h3>STOCK EDITOR ({articles.length} articles)</h3>
					<Styled.StockEditorActions>
						<Styled.ResetButton onClick={handleReset}>Reset</Styled.ResetButton>
						<button onClick={onClose}>Close</button>
					</Styled.StockEditorActions>
				</Styled.StockEditorHeader>
				{selectedSkus.size > 0 && (
					<Styled.SelectionActions>
						<span>{selectedSkus.size} selected</span>
						<button onClick={handleSelectAll}>Select all</button>
						<button onClick={handleSelectNone}>Deselect</button>
						<button onClick={() => handleBulkToggleStock(true)}>
							Set in stock
						</button>
						<button onClick={() => handleBulkToggleStock(false)}>
							Set out of stock
						</button>
					</Styled.SelectionActions>
				)}
				<Styled.StockEditorBody>
					<Styled.StockTable>
						<thead>
							<tr>
								{SORT_COLUMNS.map((col) => (
									<th key={col.key} onClick={() => handleSort(col.key)}>
										{col.label}
										{sortIndicator(col.key)}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{sortedArticles.map((article, sortedIndex) => (
								<Styled.StockRow
									key={article.sku}
									inStock={article.inStock}
									selected={selectedSkus.has(article.sku)}
									onClick={(e) => handleRowClick(article.sku, sortedIndex, e)}
								>
									<td>{article.sku}</td>
									<td>{article.name}</td>
									<Styled.EditableCell>
										<input
											type="number"
											value={article.priceWithVat}
											onChange={(e) =>
												handlePriceChange(
													article.sku,
													"priceWithVat",
													e.target.value,
												)
											}
										/>
									</Styled.EditableCell>
									<Styled.EditableCell>
										<input
											type="number"
											value={article.priceWithoutVat}
											onChange={(e) =>
												handlePriceChange(
													article.sku,
													"priceWithoutVat",
													e.target.value,
												)
											}
										/>
									</Styled.EditableCell>
									<td>
										<Styled.ProductTag>
											{article.productKey || "-"}
										</Styled.ProductTag>
									</td>
									<td>
										<Styled.InStockLabel>
											<input
												type="checkbox"
												checked={article.inStock}
												onChange={() => handleToggleInStock(article.sku)}
											/>
											inStock
										</Styled.InStockLabel>
									</td>
								</Styled.StockRow>
							))}
						</tbody>
					</Styled.StockTable>
				</Styled.StockEditorBody>
			</Styled.StockEditorPanel>
		</Styled.StockEditorOverlay>
	);
};

function apiArticlesToWorking(apiArticles: ApiArticles): WorkingArticle[] {
	const reverseTable = getReverseSkuTable();
	return Object.entries(apiArticles).map(([sku, article]) => ({
		sku,
		name: article.name,
		priceWithVat: article.priceWithVat,
		priceWithoutVat: article.priceWithoutVat,
		ean: article.ean,
		vat: article.vat,
		packSize: article.packSize,
		productKey: reverseTable[sku] ?? "",
		inStock: article.priceWithVat !== 0 && article.priceWithoutVat !== 0,
	}));
}

function workingToApiArticles(articles: WorkingArticle[]): ApiArticles {
	const result: ApiArticles = {};
	for (const a of articles) {
		if (!a.inStock) {
			continue;
		}
		result[a.sku] = {
			ean: a.ean,
			priceWithVat: a.priceWithVat,
			priceWithoutVat: a.priceWithoutVat,
			vat: a.vat,
			packSize: a.packSize,
			name: a.name,
			sku: a.sku,
		};
	}
	return result;
}

export const DeveloperPanel: FunctionComponent = () => {
	const storageKey = "developer-panel";
	const defaultPanelState: DeveloperPanelState = {
		panel: {
			visible: false,
		},
		widget: {
			selected: widgets[0],
			mounted: false,
			visible: false,
		},
	};

	const { modals } = useUiData();
	const { addApiNotification } = useContext(ApiDataContext);
	const devTools = useDevTools();

	const [panelState, setPanelState] =
		useState<DeveloperPanelState>(loadPanelState);
	const [stockEditorOpen, setStockEditorOpen] = useState(false);
	const [notificationCase, setNotificationCase] = useState<NotificationCase>(
		notificationCases[0],
	);

	function loadPanelState() {
		const storedJSON = window.sessionStorage.getItem(storageKey);
		const storedState = storedJSON ? JSON.parse(storedJSON) : null;

		return storedState
			? Object.assign(defaultPanelState, storedState)
			: defaultPanelState;
	}

	const panelToggle = () =>
		setPanelState({
			...panelState,
			panel: {
				...panelState.panel,
				visible: !panelState.panel.visible,
			},
		});

	const savePanelState = useDebounced(() => {
		window.sessionStorage.setItem(storageKey, JSON.stringify(panelState));
	}, 300);

	useEffect(() => {
		savePanelState();
	}, [savePanelState]);

	const dialog = () => {
		modals.show(<ConfirmModal title="Modal dialog">{longLorem}</ConfirmModal>);
	};

	const notify = () =>
		addApiNotification({ ...apiNotificationCases[notificationCase] });

	return (
		<>
			<Styled.Root visible={panelState.panel.visible}>
				<Styled.BarRow>
					<Styled.BarSection>
						<select
							value={panelState.widget.selected}
							onChange={(e) => {
								setPanelState({
									...panelState,
									widget: {
										...panelState.widget,
										selected: e.target.value as WidgetOption,
										mounted: true,
										visible: true,
									},
								});
							}}
						>
							{widgets.map((widget, index) => (
								<option key={index} value={widget}>
									{panelState.widget.selected === widget ? "\u2611" : "\u2610"}{" "}
									{widget}
								</option>
							))}
						</select>
						<button
							disabled={panelState.widget.selected === "none"}
							onClick={() =>
								setPanelState({
									...panelState,
									widget: {
										...panelState.widget,
										mounted: !panelState.widget.mounted,
										visible: !panelState.widget.mounted,
									},
								})
							}
						>
							{panelState.widget.mounted ? "Unmount" : "Mount"}
						</button>
						<button
							disabled={
								panelState.widget.selected === "none" ||
								!panelState.widget.mounted
							}
							onClick={() =>
								setPanelState({
									...panelState,
									widget: {
										...panelState.widget,
										visible: !panelState.widget.visible,
									},
								})
							}
						>
							{panelState.widget.visible ? "Hide" : "Show"}
						</button>
					</Styled.BarSection>
					<Styled.BarSection>
						<button onClick={dialog}>MODAL</button>
					</Styled.BarSection>
					<Styled.BarSection>
						<select
							value={notificationCase}
							onChange={(e) =>
								setNotificationCase(e.target.value as NotificationCase)
							}
						>
							{notificationCases.map((option) => (
								<option key={option} value={option}>
									{option}
								</option>
							))}
						</select>
						<button onClick={notify}>NOTIFICATION</button>
					</Styled.BarSection>
					{devTools?.stockEditor && (
						<Styled.BarSection>
							<button onClick={() => setStockEditorOpen(true)}>STOCK</button>
						</Styled.BarSection>
					)}
				</Styled.BarRow>
				<Styled.Handle onClick={panelToggle}>&nbsp;</Styled.Handle>
			</Styled.Root>
			{stockEditorOpen && (
				<StockEditor onClose={() => setStockEditorOpen(false)} />
			)}
		</>
	);
};
